/**
 * Click-to-open popover that explains what "Minimum coverage" actually buys the
 * renter — third-party liability only, with a clear covered/excluded list,
 * country-specific statutory limits, and an inline upsell CTA to the cheapest
 * vendor-defined insurance plan (when one exists).
 *
 * Mounted as a small ⓘ button next to the "Minimum coverage" radio inside
 * `BookingWidget.jsx`. Tap-to-open (works on mobile, unlike hover tooltips).
 */
import * as React from "react";
import { Info, ShieldCheck, X, ArrowUpRight } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import Money from "@/components/Money";
import { useTranslation } from "react-i18next";

// Coarse EU statutory third-party liability minimums (approximate, in EUR).
// Sources are public insurance regulators (EIOPA / national); these are the
// floors — actual policies are usually 10-100× higher. We surface them so
// renters understand "minimum coverage" is a floor, not a ceiling.
const TPL_LIMITS = {
  Germany: { bodily: "€8.5M", property: "€1.5M" },
  France: { bodily: "Unlimited", property: "€1.5M" },
  Italy: { bodily: "€6.5M", property: "€1.3M" },
  Spain: { bodily: "€72M", property: "€18M" },
  Albania: { bodily: "€800k", property: "€200k" },
  Austria: { bodily: "€7.6M", property: "€1.3M" },
  Belgium: { bodily: "Unlimited", property: "€100M" },
  Netherlands: { bodily: "€7.0M", property: "€1.5M" },
  Portugal: { bodily: "€7.5M", property: "€1.3M" },
  Greece: { bodily: "€1.3M", property: "€1.3M" },
};

/**
 * Extract a country name from a free-form location string like "Munich, Germany"
 * or "Tirana, Albania". Falls back to null when nothing recognizable matches.
 */
function _countryFromLocation(loc) {
  if (!loc || typeof loc !== "string") return null;
  const parts = loc.split(",").map((s) => s.trim());
  const tail = parts[parts.length - 1];
  return TPL_LIMITS[tail] ? tail : null;
}

/**
 * Pick the cheapest vendor-defined plan as the "recommended upgrade". Tied
 * prices → first by definition order (which is how the vendor saved them).
 */
function _recommendedPlan(plans) {
  if (!Array.isArray(plans) || plans.length === 0) return null;
  return [...plans].sort((a, b) => (Number(a.price_per_day) || 0) - (Number(b.price_per_day) || 0))[0];
}

export function InsuranceCoveragePopover({ car, onUpgrade }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const country = _countryFromLocation(car?.location);
  const limits = country ? TPL_LIMITS[country] : null;
  const recommended = _recommendedPlan(car?.insurance_plans);

  const handleUpgrade = () => {
    if (recommended && onUpgrade) onUpgrade(recommended.id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            // Prevent the parent radio-button from also flipping selection
            // when the renter just wants more info.
            e.stopPropagation();
          }}
          className="inline-flex items-center justify-center h-5 w-5 rounded-full text-neutral-400 hover:text-blue-600 hover:bg-blue-50 transition"
          aria-label={t("insurancePopover.aria")}
          data-testid="insurance-info-trigger"
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-80 p-0 overflow-hidden"
        data-testid="insurance-info-popover"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-4 pt-3.5 pb-2 border-b border-border bg-neutral-50">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <h4 className="text-sm font-semibold">{t("insurancePopover.title")}</h4>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-xs">
          <p className="text-neutral-600 leading-relaxed">{t("insurancePopover.intro")}</p>

          <div>
            <div className="font-semibold text-emerald-700 mb-1">{t("insurancePopover.coveredTitle")}</div>
            <ul className="space-y-0.5 text-neutral-700">
              <li>· {t("insurancePopover.coveredBodily")}</li>
              <li>· {t("insurancePopover.coveredProperty")}</li>
            </ul>
          </div>

          <div>
            <div className="font-semibold text-rose-700 mb-1">{t("insurancePopover.notCoveredTitle")}</div>
            <ul className="space-y-0.5 text-neutral-700">
              <li>· {t("insurancePopover.notCoveredOwnCar")}</li>
              <li>· {t("insurancePopover.notCoveredTheft")}</li>
              <li>· {t("insurancePopover.notCoveredGlass")}</li>
              <li>· {t("insurancePopover.notCoveredBelongings")}</li>
            </ul>
          </div>

          {limits ? (
            <div className="rounded-lg bg-blue-50 border border-blue-100 px-3 py-2">
              <div className="font-semibold text-blue-900 mb-0.5">
                {t("insurancePopover.limitsTitle", { country })}
              </div>
              <div className="text-neutral-700 leading-snug">
                {t("insurancePopover.limitsBodily")}: <span className="font-medium">{limits.bodily}</span>
                <span className="mx-1.5 text-neutral-400">·</span>
                {t("insurancePopover.limitsProperty")}: <span className="font-medium">{limits.property}</span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-1 italic">{t("insurancePopover.limitsNote")}</div>
            </div>
          ) : (
            <div className="rounded-lg bg-neutral-50 border border-border px-3 py-2 text-neutral-600 italic">
              {t("insurancePopover.limitsUnknown")}
            </div>
          )}
        </div>

        {recommended && (
          <button
            type="button"
            onClick={handleUpgrade}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition"
            data-testid="insurance-upgrade-cta"
          >
            <div className="text-left leading-tight">
              <div className="font-semibold">{t("insurancePopover.upgradeTitle", { plan: recommended.name })}</div>
              <div className="text-blue-100 text-[11px] mt-0.5">{t("insurancePopover.upgradeSubtitle")}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <span className="font-semibold"><Money amount={recommended.price_per_day} /></span>
              <span className="text-blue-100">/{t("insurancePopover.dayShort")}</span>
              <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </div>
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default InsuranceCoveragePopover;
